import { ReactNode } from 'react';
import { Link, Typography, Box } from '@mui/material';

/**
 * Parses text and converts URLs, emails, and phone numbers into clickable links
 */
export function parseTextWithLinks(text: string): ReactNode[] {
  // Combined regex for URLs, emails, and phone numbers
  const linkRegex = /(https?:\/\/[^\s]+)|([a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})|(\+?[\d][\d\s\-().]{7,}[\d])/g;

  const parts: ReactNode[] = [];
  let lastIndex = 0;
  let match;
  let keyIndex = 0;

  while ((match = linkRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }

    const [fullMatch, url, email, phone] = match;

    if (url) {
      parts.push(
        <Link
          key={keyIndex++}
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          underline="hover"
          color="primary"
        >
          {url}
        </Link>
      );
    } else if (email) {
      parts.push(
        <Link
          key={keyIndex++}
          href={`mailto:${email}`}
          underline="hover"
          color="primary"
        >
          {email}
        </Link>
      );
    } else if (phone) {
      const cleanPhone = phone.replace(/[\s\-().]/g, '');
      parts.push(
        <Link
          key={keyIndex++}
          href={`tel:${cleanPhone}`}
          underline="hover"
          color="primary"
        >
          {phone}
        </Link>
      );
    }

    lastIndex = match.index + fullMatch.length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}

interface FormattedTextProps {
  text: string;
  variant?: 'body1' | 'body2' | 'inherit';
  color?: string;
}

/**
 * Component that renders text with newline support and auto-linked URLs, emails, and phone numbers
 */
export function FormattedText({ text, variant = 'inherit', color }: FormattedTextProps) {
  const lines = text.split('\n').filter(line => line.trim());

  if (lines.length === 1) {
    return (
      <Typography component="span" variant={variant} color={color}>
        {parseTextWithLinks(lines[0])}
      </Typography>
    );
  }

  return (
    <Box component="span">
      {lines.map((line, index) => (
        <Typography key={index} component="div" variant={variant} color={color}>
          {parseTextWithLinks(line)}
        </Typography>
      ))}
    </Box>
  );
}
